import { useState } from 'react';
import styled from 'styled-components';

import { SectionContainer } from 'components/DetailsView/Section';
import { TotemLightBox, type TotemLightboxImage } from 'components/TotemLightBox';
import { type GetTicketQuery } from 'data/__generated__';

import { TICKET_ACTION_TYPE_LABELS, formatTicketDatetime } from '../helpers';

export const TicketActionsSection = ({ ticket }: { ticket: GetTicketQuery['ticket'] }) => {
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState<TotemLightboxImage[]>([]);
    const [initialImageIndex, setInitialImageIndex] = useState(0);

    const photos = ticket.actions
        .filter((action) => action.photoUrl)
        .map((action) => ({
            url: action.photoUrl ?? '',
            label: action.message,
        }));

    const handlePhotoClick = (photoUrl: string) => {
        const photoIndex = photos.findIndex((photo) => photo.url === photoUrl);
        setLightboxImages(photos);
        setInitialImageIndex(photoIndex >= 0 ? photoIndex : 0);
        setIsLightboxOpen(true);
    };

    return (
        <SectionContainer title="Actions Totem" isInitiallyOpen>
            <>
                {ticket.actions.length === 0 ? (
                    <EmptyMessage>Aucune action terrain enregistrée.</EmptyMessage>
                ) : (
                    <ActionsList data-test="ticket-actions">
                        {ticket.actions.map((action, index) => (
                            <ActionItem key={`${action.actionType}-${action.createdAt}-${index}`}>
                                <ActionMeta>
                                    {formatTicketDatetime(action.createdAt)} —{' '}
                                    {TICKET_ACTION_TYPE_LABELS[action.actionType]}
                                </ActionMeta>
                                <ActionMessage>{action.message}</ActionMessage>
                                {action.photoUrl ? (
                                    <Photo
                                        src={action.photoUrl}
                                        alt={action.message}
                                        onClick={() => handlePhotoClick(action.photoUrl ?? '')}
                                    />
                                ) : null}
                            </ActionItem>
                        ))}
                    </ActionsList>
                )}
                <TotemLightBox
                    images={lightboxImages}
                    initialImageIndex={initialImageIndex}
                    isOpen={isLightboxOpen}
                    setIsOpen={setIsLightboxOpen}
                />
            </>
        </SectionContainer>
    );
};

const ActionsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ActionItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ActionMeta = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.infoTextColor};
`;

const ActionMessage = styled.span`
    font-size: 14px;
`;

const Photo = styled.img`
    max-width: 200px;
    max-height: 200px;
    object-fit: contain;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        opacity: 0.8;
    }
`;

const EmptyMessage = styled.div`
    padding: 8px 0;
    color: ${({ theme }) => theme.infoTextColor};
`;
